import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';

import firstWordCapitalized from 'common/util/firstWordCapitalized';

import { SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM } from 'api/utilityActionTypes';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  createFormValidationFn,
  createInitialValues,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrSeparator from 'client/components/SrSeparator';
import SrModalContent from 'client/components/SrModalContent';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

const getFormValues = ({ utilityActivationItem, hasAccountId, setHasAccountId }) => {
  const availableProviders = utilityActivationItem?.availableProviders || [];
  const providerOptions = availableProviders.map((provider) => {
    return {
      value: provider.id,
      label: provider.title,
    };
  });

  const shouldShowProofOfPurchase = utilityActivationItem.requiresProofOfPurchase || !hasAccountId;

  return [
    {
      id: 'utilityProviderId',
      title: 'Provider Company Name',
      type: 'select',
      properties: {
        placeholder: '--',
        choices: providerOptions,
      },
      validations: ['required'],
    },
    {
      id: 'accountNumber',
      title: 'Account ID',
      titleClassName: !hasAccountId ? 'inactive' : null,
      titleToggle: {
        text: hasAccountId ? 'No ID available?' : 'Have the ID?',
        onClick: () => {
          setHasAccountId(!hasAccountId);
        },
      },
      type: 'short_text',
      properties: {
        placeholder: hasAccountId ? 'enter account ID...' : 'no account ID available',
        disabled: !hasAccountId,
        style: !hasAccountId ? { color: '#D4D6D8', opacity: 0.5 } : {},
      },
      validations: hasAccountId ? ['required'] : [],
    },
    showFieldIf(shouldShowProofOfPurchase, {
      id: 'proofOfPurchaseUrl',
      title: 'Upload Proof of Purchase',
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath:
          `utility-activations/${utilityActivationItem.utilityActivationId}` +
          `/item-${utilityActivationItem.id}/proof-of-purchase-{uuid}`,
      },
      validations: ['required'],
    }),
    {
      id: 'activationDate',
      title: 'Activation Date',
      type: 'date_picker',
      properties: {
        placeholder: 'select activation date...',
      },
      validations: ['required'],
    },
  ];
};

export const normalizeUtilityActivationItemFormValues = ({
  formValues,
  hasAccountId,
  requiresProofOfPurchase,
}) => {
  if (!hasAccountId) {
    formValues.accountNumber = null;
  } else if (!requiresProofOfPurchase) {
    formValues.proofOfPurchaseUrl = null;
  }

  return formValues;
};

const SrUtilityActivationEditItemModal = ({
  utilityActivationItem,
  address,
  onEditCallback,
  onClose,
}) => {
  const [hasAccountId, setHasAccountId] = useState(
    utilityActivationItem.accountNumber || !utilityActivationItem.proofOfPurchaseUrl,
  );

  const dispatch = useDispatch();

  const onSubmit = (formValues) => {
    normalizeUtilityActivationItemFormValues({
      formValues,
      hasAccountId,
      requiresProofOfPurchase: utilityActivationItem.requiresProofOfPurchase,
    });

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM,
      payload: {
        id: utilityActivationItem.id,
        updates: formValues,
      },
    }).then(() => {
      if (onEditCallback) {
        return onEditCallback();
      }
      onClose();
    });
  };

  return (
    <SrDarkModal onClose={onClose}>
      <SrModalContent style={{ width: 412, marginTop: 4 }}>
        <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <SrText
            weight="800"
            font="greycliff"
            fontSize="26px"
            lineHeight="36px"
            letterSpacing="0.5"
          >
            {firstWordCapitalized(utilityActivationItem.type.label)}
            &nbsp;utility&nbsp;
            <img
              width="22px"
              height="22px"
              title="Utility Icon"
              alt="Utility Icon"
              src={utilityActivationItem?.type?.iconUrl}
            />
          </SrText>
          <SrBox marginTop="12px">
            <SrText
              weight="800"
              fontSize="16px"
              font="greycliff"
              lineHeight="19px"
              letterSpacing="0.5"
              display="block"
            >
              {address}
            </SrText>
          </SrBox>
          <SrBox width="328px" marginTop="7px">
            <SrText
              weight="600"
              fontSize="16px"
              font="greycliff"
              lineHeight="19px"
              letterSpacing="0.5"
              display="block"
              align="center"
            >
              This will mark this utility as completed, and the tenant will stop receiving
              notifications.
            </SrText>
          </SrBox>
        </SrBox>
        <SrSeparator marginTop="20px" marginBottom="8px" />
        <SrBox display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <Formik
            initialValues={createInitialValues({
              formFields: getFormValues({
                utilityActivationItem,
                hasAccountId,
                setHasAccountId,
              }),
              defaultValues: {
                utilityProviderId: utilityActivationItem.utilityProviderId,
                accountNumber: utilityActivationItem.accountNumber,
                proofOfPurchaseUrl: utilityActivationItem.proofOfPurchaseUrl,
                activationDate: utilityActivationItem.activationDate,
              },
            })}
            validate={(formValues) => {
              return createFormValidationFn(
                getFormValues({
                  utilityActivationItem,
                  hasAccountId,
                  setHasAccountId,
                }),
              )(formValues);
            }}
            onSubmit={onSubmit}
            render={(formikBag) => {
              const formFields = getFormValues({
                utilityActivationItem,
                hasAccountId,
                setHasAccountId,
              });

              return (
                <Form style={{ width: '100%', maxWidth: '328px' }}>
                  <SrBox margin="20px 0">
                    {formFields.map((formField) => {
                      return (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={SR_FIELD_THEME_ADOBE}
                        />
                      );
                    })}
                  </SrBox>
                  <SrBox className="submitButtonContainer">
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREEN}
                      onClick={formikBag.submitForm}
                      disabled={!formikBag.isValid}
                    >
                      Complete
                    </SrButton>
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREY}
                      margin="12px 0px"
                      onClick={onClose}
                    >
                      Cancel
                    </SrButton>
                  </SrBox>
                </Form>
              );
            }}
          />
        </SrBox>
      </SrModalContent>
    </SrDarkModal>
  );
};

SrUtilityActivationEditItemModal.propTypes = {
  utilityActivationItem: PropTypes.object,
  address: PropTypes.string,
  onEditCallback: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

SrUtilityActivationEditItemModal.displayName = 'SrUtilityActivationEditItemModal';

export default SrUtilityActivationEditItemModal;
